import { App, Button, Card, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Tariff } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWriteTarifas } from '@/layouts/navigation';
import { tariffApi } from '../api/tariffApi';
import { TariffTable } from '../components/TariffTable';
import { useTariffs } from '../hooks/useTariffs';

/**
 * Listado de Tarifas (US-2.4, `/tarifas`) — paginación server-side vía `useTariffs`.
 * La escritura es solo de `SUPER_ADMIN` (contrato): el alta, la edición y la
 * eliminación se ocultan al resto de roles para no ofrecer un 403.
 */
export function TariffsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message } = App.useApp();
  const tariffs = useTariffs();

  const canWrite = canWriteTarifas(user?.role);
  const goToCreate = () => navigate('/tarifas/nueva');

  const remove = async (tariff: Tariff) => {
    try {
      await tariffApi.remove(tariff.id);
      message.success('Tarifa eliminada.');
      // Si se borró el último elemento de una página > 0, retrocede una página para
      // no dejar un empty-state engañoso.
      if (tariffs.data.length === 1 && tariffs.offset > 0) {
        tariffs.setOffset(Math.max(0, tariffs.offset - tariffs.limit));
      } else {
        tariffs.refresh();
      }
    } catch (caught) {
      message.error(caught instanceof ApiError ? caught.message : 'No se pudo eliminar la tarifa.');
    }
  };

  return (
    <div className="tarifas-page">
      <div className="tarifas-page__header">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Tarifas
          </Typography.Title>
          <Typography.Text type="secondary">
            Gestiona las tarifas planas, TOU (pico-valle) y por tiempo (Idle Fee) de la red.
          </Typography.Text>
        </div>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={goToCreate}>
            Nueva tarifa
          </Button>
        )}
      </div>

      <Card>
        <TariffTable
          tariffs={tariffs.data}
          meta={tariffs.meta}
          loading={tariffs.loading}
          error={tariffs.error}
          filters={tariffs.filters}
          canWrite={canWrite}
          onFiltersChange={tariffs.setFilters}
          onOffsetChange={tariffs.setOffset}
          onRetry={tariffs.refresh}
          onCreate={goToCreate}
          onSelect={(tariff) => navigate(`/tarifas/${encodeURIComponent(tariff.id)}`)}
          onEdit={(tariff) => navigate(`/tarifas/${encodeURIComponent(tariff.id)}/editar`)}
          onDelete={(tariff) => void remove(tariff)}
        />
      </Card>
    </div>
  );
}
